/** Canonical error class for utils/microservices/tools throw sites: a stable `code` field for programmatic dispatch instead of brittle `error.message.includes(...)`, the original cause preserved so async stacks survive re-throws, and typed `details` that never leak onto the message. `catchToAdapterError` copies `code`, `details`, `status` and a JSON-safe cause summary so the structured shape survives the catch. Lives on the const/ floor, not under utils/, because QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001 bans every utils/->utils/ value import with no allowlist: a layer-floor class every layer may import has to sit where every layer may import from. */

import { isRecordValue } from '@federa/const/record-guards';
import { isValidString } from '@federa/const/string-guards';
interface BaseErrorOptions {
	/** Stable error code (see ERROR_CODES taxonomy). */
	code: string;
	/** Human-readable message (single line, NO PII). */
	message: string;
	/** Original error/value being wrapped; preserved on `.cause`. */
	cause?: unknown;
	details?: Record<string, unknown>;
	/** HTTP status hint for adapter mapping; defaults at toAdapterError. */
	status?: number;
}

/** Extends native Error so existing `instanceof Error` and `getErrorMessage` call sites keep working unchanged. */
export class BaseError extends Error {
	public readonly code: string;
	public readonly details?: Record<string, unknown>;
	public readonly status?: number;
	// Native `cause` is shadowed as a public FIELD so it serializes through structured-clone/JSON paths the same way `code` and `details` do.
	public override readonly cause?: unknown;

	constructor(opts: BaseErrorOptions) {
		// Also passed to the native Error options so V8 records it on the stack chain.
		super(opts.message, opts.cause === undefined ? undefined : { cause: opts.cause });
		this.name = 'BaseError';
		this.code = opts.code;
		if (opts.cause !== undefined) this.cause = opts.cause;
		if (opts.details !== undefined) this.details = opts.details;
		if (opts.status !== undefined) this.status = opts.status;
	}
}

/**
 * `BaseError` as a FUNCTION VALUE, for the deps records that inject an error constructor rather than importing the
 * class (`SafeStorageDeps.newError`, `utils/durable-objects/safe-storage.ts`). Every binding site had spelled the same
 * `(opts) => new BaseError(opts)` adapter inline, and `tools/` siblings may not import one another
 * (`QUAL-NO-CROSS-TOOL-IMPORTS-001`), so the copies could only converge on the const/ floor the class already sits on.
 * The deps-record SEAM is untouched -- this is the one adapter those records now name instead of re-declaring.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): constructs the `BaseError` class declared in this same leaf.
 */
export function newBaseError(opts: BaseErrorOptions): BaseError {
	return new BaseError(opts);
}

/**
 * Type guard for BaseError that survives bundler boundaries.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): recognizer for the `BaseError` class declared in this same leaf,
 * homed in const/ because utils/ and const/ consumers cannot import a utils sibling
 * (QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001 bans every utils/->utils/ value import).
 */
export function isBaseError(value: unknown): value is BaseError {
	return (
		value instanceof BaseError ||
		(isRecordValue(value) && (value as { name?: unknown }).name === 'BaseError' && typeof (value as { code?: unknown }).code === 'string')
	);
}

/**
 * The typed `details` a caught value carries when it is a `BaseError`, else undefined -- the read a catch arm makes
 * before it branches on a captured upstream fact (a status, a body, a provider code).
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): reads the `details` member the `BaseError` class in this same leaf
 * defines, through the recognizer beside it.
 */
export function baseErrorDetails(error: unknown): Record<string, unknown> | undefined {
	return isBaseError(error) ? error.details : undefined;
}

/**
 * Message plus the raw upstream response body when one was captured on `BaseError.details.body`. BaseError keeps that
 * body off `.message` by design; surface it at log/return boundaries so opaque `HTTP <status>` failures stay
 * diagnosable. Falls back to the plain message for non-BaseError values.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): reads the `details.body` shape the `BaseError` class in this same
 * leaf defines, homed in const/ because its consumers sit below the utils layer floor and cannot import a utils sibling.
 */
export function getErrorMessageWithBody(error: unknown): string {
	const base = error instanceof Error ? error.message : typeof error === 'string' ? error : String(error);
	const body = baseErrorDetails(error)?.body;
	return isValidString(body) ? `${base} -- ${body}` : base;
}
